import { QueryHandlingService, WatchQueryRef } from '@akebono/core';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import {
  LotListDataGQL,
  LotsInspectedGQL,
  LotsInspectedQuery,
  LotsInspectedQueryVariables,
  LotDataFragment,
  LotSource,
  LotTypeEnum,
} from '../../graphql/service/graphql-auto-main-service';
import {
  AutoLot,
  LotsListGQL,
  LotsListQuery,
  LotsListQueryVariables,
} from '../../graphql/service/graphql-cars-default-service';

type LotIdSource = string | Pick<AutoLot, 'id'>;

@Injectable()
export class LotListDataService {
  constructor(
    private qhs: QueryHandlingService,
    private lotsListGQL: LotsListGQL,
    private lotListDataGQL: LotListDataGQL,
    private lotsInspectedGQL: LotsInspectedGQL,
  ) {}

  watchLots(
    variables: LotsListQueryVariables,
  ): WatchQueryRef<LotsListQuery, LotsListQueryVariables> {
    return this.qhs.watch(this.lotsListGQL, variables, 'network-only');
  }

  fetchData(
    lots: LotIdSource[],
    lotType: LotTypeEnum,
    lotSource: LotSource,
  ): Observable<Map<string, LotDataFragment>> {
    const lotIds = this.getLotIds(lots);

    if (!lotIds.length) {
      return of(new Map());
    }

    return this.qhs
      .fetch(
        this.lotListDataGQL,
        {
          lotIds,
          lotSource,
          subgroup: lotType,
        },
        'network-only',
      )
      .data.pipe(
        map((data) => data.lots.nodes),
        map((items) => new Map(items.map((item) => [item.id, item]))),
        shareReplay(1),
      );
  }

  fetchInspectedData(
    lots: LotIdSource[],
    lotType: LotTypeEnum,
    lotSource: LotSource,
  ): Observable<Map<string, boolean>> {
    const lotIds = this.getLotIds(lots);

    if (!lotIds.length) {
      return of(new Map());
    }

    return this.qhs
      .fetch<LotsInspectedQuery, LotsInspectedQueryVariables>(
        this.lotsInspectedGQL,
        {
          lots: lotIds.map((id) => ({
            id,
            subgroup: lotType,
            source: lotSource,
          })),
        },
        'network-only',
      )
      .data.pipe(
        map((data) => data.lotsInspected),
        map((items) => new Map(items.map((item) => [item.id, item.inspected]))),
        shareReplay(1),
      );
  }

  private getLotIds(lots: LotIdSource[]): string[] {
    return lots.map((lot) => (typeof lot === 'string' ? lot : lot.id));
  }
}
